"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  type MouseEventHandler,
  useEffect,
  useRef,
  useTransition,
} from "react";
import { createPortal } from "react-dom";

import { TtvEditorLoading } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/TtvEditorLoading";

export function TtvNavigationLink({
  href,
  className,
  children,
  onClick,
  onNavigationComplete,
}: {
  href: string;
  className: string;
  children: React.ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  onNavigationComplete?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const hasPendingNavigation = useRef(false);
  const loadingTarget = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isPending && hasPendingNavigation.current) {
      hasPendingNavigation.current = false;
      onNavigationComplete?.();
    }
  }, [isPending, onNavigationComplete]);

  return (
    <>
      <Link
        href={href}
        className={className}
        aria-busy={isPending}
        onClick={(event) => {
          onClick?.(event);
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            pathname === href
          ) {
            if (!event.defaultPrevented) {
              onNavigationComplete?.();
            }
            return;
          }
          // Keep the route's loading feedback visible from the gesture, including cached server transitions that never display loading.tsx.
          event.preventDefault();
          hasPendingNavigation.current = true;
          loadingTarget.current =
            event.currentTarget.closest<HTMLElement>('[role="dialog"]') ??
            document.body;
          startTransition(() => router.push(href));
        }}
      >
        {children}
      </Link>
      {isPending &&
        loadingTarget.current !== null &&
        createPortal(
          <div className="fixed inset-0 z-[600] flex">
            <TtvEditorLoading />
          </div>,
          loadingTarget.current
        )}
    </>
  );
}
